import { useNavigate, useParams } from 'react-router-dom';
import { StudyPage } from '../../features/study/StudyPage';
import { aiStudyTab } from '../../features/actions/aiTab';
import { discussionDock } from '../../features/discussion/DiscussionDock';
import { ViewerBottomBar } from '../../features/viewer-controls/ViewerBottomBar';
import { useApplyViewerTheme, useViewerLight } from '../../features/viewer-controls/viewerTheme';
import { useViewerGlideSetting } from '../../features/viewer-controls/viewerGlide';
import { useViewerSharpMotionSetting, useViewerSharpnessSetting } from '../../features/viewer-controls/viewerSharpness';
import { useViewerXrayAutoWindowSetting } from '../../features/viewer-controls/xrayAutoWindow';
import { ConfirmHost } from '../../community/ui/Confirm';
import { ToastHost } from '../../community/ui/toast';

/** The AI tab between Annotations and Info. */
const TABS = [aiStudyTab];
/** The Discussion under the tabs; enterprise has no guests, so nobody is sent to sign in. */
const DOCK = discussionDock();
/** Where the bar's TileRiver mark leads here: the worklist. */
const HOME = { to: '/', label: 'Studies' };

/**
 * The enterprise study page: the shared viewer, with "Edit details" in its
 * header for somebody who may write the study (StudyPage decides when), the
 * AI tab, the Discussion dock and the viewer's bottom bar (the theme switch,
 * Shortcuts, the colour profile). The editor is addressed by UID, like the
 * study.
 *
 * The viewer is the same page in both editions (decided 2026-10-04; this
 * shell differs by its top bar and the PHI it shows). `tr-scope` gives the
 * page the community tokens, page colour and typeface (community.css); the
 * theme the person chose at the bottom of the viewer, or in the community's
 * Settings, is the account's and applies here too, as does the glide choice.
 * The toast and confirm hosts are mounted here because this shell has none
 * of its own.
 */
export function StudyRoute() {
  const nav = useNavigate();
  const { studyUID } = useParams<{ studyUID: string }>();
  const [light, setLight] = useViewerLight();
  useApplyViewerTheme(light);
  useViewerGlideSetting();
  useViewerSharpnessSetting();
  // Sharpness in Motion is set in Settings, but the account's choice must reach the viewer on any device.
  useViewerSharpMotionSetting();
  // Opening X-rays on the automatic window, likewise set in Settings.
  useViewerXrayAutoWindowSetting();
  return (
    <div className="tr-scope flex h-full flex-col">
      <StudyPage
        extraTabs={TABS}
        dock={DOCK}
        bottomBar={<ViewerBottomBar light={light} onChange={setLight} home={HOME} />}
        onEditDetails={() => nav(`/studies/${encodeURIComponent(studyUID ?? '')}/details`)}
      />
      <ToastHost />
      <ConfirmHost />
    </div>
  );
}
